import { useMemo } from "react";
import { scaleOrdinal } from "@visx/scale";
import { colorVar, type Color } from "../../lib/colors";
import type { Omittable } from "../../lib/props";

/**
 * The semantic color a chart author may pick, in place of a raw hex/rgb. Mapped
 * to a `var()` string by {@link useChartColors}. `chart1`–`chart5` are the
 * categorical series ramp; the rest are the library `Color` union — role colors
 * for accents (a threshold line, a KPI arc) plus the palette names entities
 * store (`"blue"`, `"teal"`, …), so a chart of stages or teams can wear each
 * entity's own color.
 */
export type ChartColorToken = ChartRampToken | Color;

/**
 * The categorical series ramp as semantic **tokens** `[chart1 … chart5]` — for a
 * chart that colors each datum itself by passing `colorToken` per mark (a Sankey
 * node, a box-plot stage), rather than getting auto-ramping from a series `dataKey`.
 * The `var()` counterpart is `useChartColors().ramp`; both are the same five
 * colors, so a token-colored card and a series-colored card stay visually in sync.
 */
export const CHART_RAMP_TOKENS = [
  "chart1",
  "chart2",
  "chart3",
  "chart4",
  "chart5",
] as const;

/** The ramp's own members, apart from the `Color` union entities store. */
export type ChartRampToken = (typeof CHART_RAMP_TOKENS)[number];

function isRampToken(token: ChartColorToken): token is ChartRampToken {
  return (CHART_RAMP_TOKENS as readonly string[]).includes(token);
}

// Ramp tokens are chart-only; everything else (role colors + the palette names
// entities store) goes through the same `colorVar` the icons and pins use, so a
// stage's bar and its icon name the same token.
function resolveChartColor(token: ChartColorToken): string;
function resolveChartColor(token?: ChartColorToken): string | undefined;
function resolveChartColor(token?: ChartColorToken): string | undefined {
  if (token === undefined) return undefined;
  return isRampToken(token) ? CHART_RAMP_VARS[token] : colorVar(token);
}

/** The ramp cycles, so every slot resolves however many series there are. */
function rampToken(slot: number): ChartRampToken {
  return CHART_RAMP_TOKENS[slot % CHART_RAMP_TOKENS.length] ?? CHART_RAMP_TOKENS[0];
}

// A single mark has no series index to ramp from, so the ramp's head is its
// color. Written as a token substitution rather than a fallback on the resolved
// string: `resolve` only answers `undefined` for an absent token, so the two
// spellings agree, and this one stays `string` for the `fill`/`stroke`
// attributes that have no `undefined` to give.
function paintChartColor(token?: ChartColorToken): string {
  return resolveChartColor(token ?? CHART_RAMP_TOKENS[0]);
}

export interface ChartColors {
  /** The categorical series ramp `[chart1 … chart5]` — feeds ordinal scales. */
  ramp: string[];
  /** Map a token to its `var()` string; `undefined` in → `undefined` out
   * (fall back to theme). A named token always resolves. */
  resolve(token: ChartColorToken): string;
  resolve(token?: ChartColorToken): string | undefined;
  /** A token's `var()` for a `fill`/`stroke`, defaulting to the ramp's head
   * when the mark names no color. Always answers a paintable string. */
  paint(token?: ChartColorToken): string;
  /** Chart background. */
  background: string;
  /** Card surface, used by tooltip chrome. */
  card: string;
  /** Primary label/foreground. */
  foreground: string;
  /** De-emphasized label (tick labels, subtitles). */
  mutedForeground: string;
  /** Grid/axis line. */
  border: string;
}

const CHART_RAMP_VARS: Record<ChartRampToken, string> = {
  chart1: "var(--chart-1)",
  chart2: "var(--chart-2)",
  chart3: "var(--chart-3)",
  chart4: "var(--chart-4)",
  chart5: "var(--chart-5)",
};

/**
 * Theme colors for chart-kit as `var()` strings. Everything a wrapper needs to
 * auto-theme itself; authors never touch these directly.
 *
 * `fill`, `stroke` and `stop-color` are CSS properties: they evaluate `var()`,
 * they work as presentation attributes, and the browser re-resolves them on a
 * `.dark` flip with no re-render and no re-paint of stale values. So this is a
 * module constant — there is nothing theme-dependent left to recompute.
 */
const CHART_COLORS: ChartColors = {
  ramp: CHART_RAMP_TOKENS.map((token) => CHART_RAMP_VARS[token]),
  resolve: resolveChartColor,
  paint: paintChartColor,
  background: "var(--background)",
  card: "var(--card)",
  foreground: "var(--foreground)",
  mutedForeground: "var(--muted-foreground)",
  border: "var(--border)",
};

export function useChartColors(): ChartColors {
  return CHART_COLORS;
}

/** One categorical datum for {@link useCategoricalColors}: its display label
 * and, when the datum IS an entity (a stage, a team, an issue type), the
 * entity-owned palette color name. `null`/`undefined` means "no owned color". */
export type CategoricalColorItem = Omittable<{
  label: string;
  color?: Color | null;
}>;

export interface CategoricalColors {
  /** Semantic assignment per item, index-aligned: the entity's own color name,
   * or the next free ramp token for colorless items. Feed to mark `colorToken`. */
  tokens: ChartColorToken[];
  /** The same assignment as `var()` strings, index-aligned. Feed to a
   * series `colorAccessor`. */
  colors: string[];
  /** `label → var()` ordinal scale over the same assignment, for `LegendOrdinal`. */
  scale: ReturnType<typeof scaleOrdinal<string, string>>;
  /** `label → token` over the same assignment — the `scale` twin, for a
   * `ChartText` that must name the token it is drawn ON. */
  token(label: string): ChartColorToken | undefined;
}

/**
 * THE categorical color assignment for entity-sliced charts. Color follows the
 * entity, never its rank: an item with an entity-owned palette color (stage,
 * team, issue type, workflow icon color) always wears it, in every widget, at
 * every sort position. Only colorless items (users, age buckets, "Other")
 * consume the `chart1`–`chart5` ramp, in fixed first-appearance order.
 * Marks, series accessors, and legends must all draw from the ONE returned
 * assignment — never a parallel `useOrdinalColorScale` next to it.
 */
export function useCategoricalColors(
  items: readonly CategoricalColorItem[],
): CategoricalColors {
  const c = useChartColors();
  return useMemo(() => {
    // One pass builds all three views, so index alignment is structural rather
    // than three separate walks that must agree.
    const tokens: ChartColorToken[] = [];
    const colors: string[] = [];
    const byLabel = new Map<string, ChartColorToken>();
    let rampSlot = 0;
    for (const item of items) {
      const token = item.color ?? rampToken(rampSlot++);
      tokens.push(token);
      colors.push(c.resolve(token));
      byLabel.set(item.label, token);
    }
    return {
      tokens,
      colors,
      scale: scaleOrdinal<string, string>({
        domain: items.map((i) => i.label),
        range: colors,
      }),
      token: (label: string) => byLabel.get(label),
    };
  }, [items, c]);
}
